import React from 'react';
import Icon from '../../../components/AppIcon';


const NotificationCard = ({ 
  notification, 
  isSelected, 
  onSelect, 
  onClick, 
  onMarkAsRead, 
  onDelete 
}) => {
  const getTypeIcon = (type) => {
    const icons = {
      match: 'Users',
      message: 'MessageSquare',
      interview: 'Calendar',
      reveal: 'Shield',
      system: 'Bell'
    };
    return icons?.[type] || 'Bell';
  };

  const getTypeColor = (type) => {
    const colors = {
      match: 'bg-purple-100 text-purple-700',
      message: 'bg-blue-100 text-blue-700',
      interview: 'bg-emerald-100 text-emerald-700',
      reveal: 'bg-amber-100 text-amber-700',
      system: 'bg-gray-100 text-gray-700'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          <Icon name="AlertCircle" size={12} />
          Urgent
        </span>
      );
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date?.getFullYear() !== now?.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleCardClick = (e) => {
    if (e?.target?.closest('.notification-actions')) return;
    onClick(notification);
  };

  return (
    <div
      className={`bg-white rounded-2xl border transition-all duration-200 hover:shadow-md ${
        notification?.isRead ? 'border-[#E5E7EB]' : 'border-[#5B2DFF] bg-purple-50/30'
      } ${
        isSelected ? 'ring-2 ring-[#5B2DFF]' : ''
      }`}
    >
      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3 md:gap-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(notification?.id)}
            className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#5B2DFF] focus:ring-[#5B2DFF]"
            onClick={(e) => e?.stopPropagation()}
          />

          <div
            className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${
              getTypeColor(notification?.type)
            }`}
          >
            <Icon name={getTypeIcon(notification?.type)} size={20} />
          </div>

          <div className="flex-1 min-w-0 cursor-pointer" onClick={handleCardClick}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className={`text-base md:text-lg font-semibold ${
                notification?.isRead ? 'text-[#6B7280]' : 'text-[#111827]'
              }`}>
                {notification?.title}
              </h3>
              {getPriorityBadge(notification?.priority)}
            </div>

            <p className={`text-sm md:text-base mb-2 line-clamp-2 ${
              notification?.isRead ? 'text-[#9CA3AF]' : 'text-[#6B7280]'
            }`}>
              {notification?.message}
            </p>

            <div className="flex items-center gap-3 text-xs md:text-sm text-[#9CA3AF]">
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                {formatTimestamp(notification?.createdAt)}
              </span>
              {!notification?.isRead && (
                <span className="flex items-center gap-1 text-[#5B2DFF] font-medium">
                  <div className="w-2 h-2 rounded-sm bg-[#5B2DFF]"></div>
                  New
                </span>
              )}
            </div>
          </div>

          <div className="notification-actions flex items-center gap-2">
            {!notification?.isRead && (
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onMarkAsRead(notification?.id);
                }}
                className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
                aria-label="Mark as read"
                title="Mark as read"
              >
                <Icon name="Check" size={18} color="#5B2DFF" />
              </button>
            )}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onDelete(notification?.id);
              }}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Delete notification"
              title="Delete"
            >
              <Icon name="Trash2" size={18} color="#EF4444" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;